import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOpsConsultMoreDetails,
  getOpsSessionDocument,
  getSessionDocument,
  OpsPastSession,
  opsSessionDownloadDocument,
  sessionDeleteDocument,
  sessionDownloadDocument,
  uploadSessionDocument,
} from '../services/ConsultMoreDetailsService';
import type {
  DocType,
  DocumentId,
  SessionDetails,
  SuccessResponse,
} from '../services/ConsultMoreDetailsService';
import { REFRESH_INTERVAL } from '@qler-view/shared_lib/src';

export const useOpsConsultMoreDetails = (consultId: number, userId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery<SessionDetails>(
    {
      queryKey: ['OpsconsultMoreDetails', consultId],
      queryFn: async () => {
        const data = await getOpsConsultMoreDetails(consultId, userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      refetchInterval: REFRESH_INTERVAL,
      enabled: !!consultId,
    }
  );

  return {
    opsConsultMoreDetails: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useOpsPastSession = (consultId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['OpsPastSessionList', consultId],
    queryFn: async () => {
      const data = await OpsPastSession(consultId);
      return data;
    },
    refetchInterval: 10000,
  });
  return {
    opsPastSessionList: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useOpsGetSessionDocuments = (
  userId: number,
  consultId: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['OpsSessionDocumentList', userId, consultId],
    queryFn: async () => {
      const data = await getOpsSessionDocument(consultId);
      return data;
    },
  });
  return {
    opsSessionDocumentList: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useOpsDownloadDocument = () => {
  return useMutation<string, Error, DocumentId>({
    mutationFn: async (docID) => {
      const data = await opsSessionDownloadDocument(docID.documentId);
      return data;
    },
  });
};

export const useUploadSessionDocument = (refetch: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, DocType>({
    mutationFn: async (user) => {
      const data = await uploadSessionDocument(user);
      return data;
    },
    onSuccess: (data) => {
      console.log('uploaded successfully', data);

      // Invalidate all queries related to today's consults
      queryClient.invalidateQueries({
        queryKey: ['SessionDocumentList'],
      });
      refetch();
    },
    onError: (error) => {
      console.error('Error accepting consult:', error);
    },
  });
};

export const useGetSessionDocuments = (userId: number, consultId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['SessionDocumentList', userId, consultId],
    queryFn: async () => {
      const data = await getSessionDocument(consultId);
      return data;
    },
  });
  return {
    SessionDocumentList: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
export const useDownloadDocument = () => {
  return useMutation<string, Error, DocumentId>({
    mutationFn: async (docID) => {
      const data = await sessionDownloadDocument(docID.documentId);
      return data;
    },
  });
};
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, DocumentId>({
    mutationFn: async (docID) => {
      const data = await sessionDeleteDocument(docID.documentId);
      return data;
    },
    onSuccess: (data) => {
      console.log('uploaded successfully', data);

      // Invalidate all queries related to today's consults
      queryClient.invalidateQueries({
        queryKey: ['SessionDocumentList'],
      });
    },
  });
};
